import axios from 'axios';
import userApi from './user';
import { loadPage } from '../helpers/NavigationHelper';

let cancelSource = axios.CancelToken.source();
let backgroundCancelSource = axios.CancelToken.source();
let refreshingPromise = null;

export default {
    logout() {
        const store = window.vuexStore;
        // Clear session key
        store.commit(
            'currentUser/setSessionKey',
            { key: null, environment: 'ERP' },
            {
                root: true,
            }
        );
        store.commit(
            'currentUser/setSessionKey',
            { key: null, environment: 'API' },
            {
                root: true,
            }
        );
        store.commit(
            'currentUser/setSessionKey',
            { key: null, environment: 'API-refresh' },
            {
                root: true,
            }
        );
        store.commit('currentUser/revokeApiCredentials', null, {
            root: true,
        });

        // Go to login with a redirect back to the current page
        const loginRedirect =
            window.newVueApp && window.newVueApp.$router.currentRoute
                ? 'login?url=' +
                  window.newVueApp.$router.currentRoute.fullPath.slice(1)
                : 'login';

        loadPage(loginRedirect);
    },

    registerInterceptors() {
        // Intercept all axios requests
        axios.interceptors.request.use(
            async (config) => {
                const store = window.vuexStore;

                let isApiRequest = false;
                // Check if the url contains [API] and if so, use the api url
                if (config.url.includes('[API]')) {
                    isApiRequest = true;

                    // Remove [API] from the url
                    config.url = config.url.replace('[API]', '');
                }

                if (config.isBackground) {
                    config.cancelToken = backgroundCancelSource.token;
                } else if (!config.cancelToken) {
                    config.cancelToken = cancelSource.token;
                    backgroundCancelSource.cancel('normal request');
                    backgroundCancelSource = axios.CancelToken.source();
                    store.dispatch(
                        'productTypesCache/refreshProductTypesDelayed',
                        { forceCheck: false, debounceTime: 3000 },
                        { root: true }
                    );
                }

                if (isApiRequest) {
                    config.baseURL = Config.apiUrl;
                } else {
                    config.baseURL = Config.url;
                }

                if (
                    isApiRequest &&
                    config.url != '/login' &&
                    isApiRefreshTokenExpired()
                ) {
                    this.logout();

                    return config;
                }

                config.headers['X-App-Version'] = Config.version;
                const sessionKeyName = isApiRequest
                    ? config.url == '/refresh-token'
                        ? 'API-refresh'
                        : 'API'
                    : 'ERP';
                let sessionKey =
                    store.getters['currentUser/getSessionKey'](sessionKeyName);

                window.refreshApiToken = refreshApiToken;

                let apiSessionKey =
                    store.getters['currentUser/getSessionKey']('API');
                if (
                    apiSessionKey &&
                    isApiTokenExpired() &&
                    config.url != '/login' &&
                    config.url != '/refresh-token'
                ) {
                    if (!refreshingPromise) {
                        refreshingPromise = refreshApiToken();
                    }
                    await refreshingPromise;

                    // Get the new session key after refreshing
                    sessionKey =
                        store.getters['currentUser/getSessionKey'](
                            sessionKeyName
                        );
                }

                if (sessionKey) {
                    config.headers['Authorization'] = 'Bearer ' + sessionKey;
                    config.headers['APIToken'] =
                        store.getters['currentUser/getSessionKey']('API');
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Intercept all axios responses
        axios.interceptors.response.use(
            (response) => {
                // Any status code that lie within the range of 2xx cause this function to trigger
                return response;
            },
            (error) => {
                // Any status codes that falls outside the range of 2xx cause this function to trigger
                if (error.response && error.response.status === 401) {
                    this.logout();
                }

                return Promise.reject(error);
            }
        );
    },
    cancelRequests(reason = 'Page change') {
        cancelSource.cancel(reason);
        cancelSource = axios.CancelToken.source();
        backgroundCancelSource.cancel(reason);
        backgroundCancelSource = axios.CancelToken.source();
    },
    createGetUrl(subdir, params) {
        if (!subdir.startsWith('/')) {
            subdir = '/' + subdir;
        }
        if (!subdir.endsWith('/')) {
            subdir += '/';
        }
        let url = Config.url + subdir;
        if (params) {
            url += '?' + new URLSearchParams(params).toString();
        }
        return url;
    },
};

function isApiTokenExpired() {
    const expiry =
        window.vuexStore.getters['currentUser/getApiCredentials'].token
            ?.expiresAt;

    if (!expiry) {
        return true;
    }

    const oneMinute = 1000 * 60;
    const cmp = new Date().getTime() + oneMinute;

    return new Date(expiry).getTime() <= cmp;
}

// Expose for debugging
window.isApiTokenExpired = isApiTokenExpired;

function isApiRefreshTokenExpired() {
    const expiry =
        window.vuexStore.getters['currentUser/getApiCredentials'].refreshToken
            ?.expiresAt;

    if (!expiry) {
        return true;
    }

    const oneMinute = 1000 * 60;
    const cmp = new Date().getTime() + oneMinute;

    return new Date(expiry).getTime() <= cmp;
}

// Expose for debugging
window.isApiRefreshTokenExpired = isApiRefreshTokenExpired;

function refreshApiToken() {
    return userApi.refreshApiToken().then((response) => {
        const store = window.vuexStore;

        const { token, refreshToken, tokenExpiresAt, refreshTokenExpiresAt } =
            response.data;

        store.commit(
            'currentUser/setSessionKey',
            { key: token, environment: 'API' },
            { root: true }
        );

        store.commit(
            'currentUser/setSessionKey',
            { key: refreshToken, environment: 'API-refresh' },
            { root: true }
        );

        store.commit(
            'currentUser/setApiCredentials',
            {
                token: {
                    value: token,
                    expiresAt: tokenExpiresAt,
                },
                refreshToken: {
                    value: refreshToken,
                    expiresAt: refreshTokenExpiresAt,
                },
            },
            { root: true }
        );

        refreshingPromise = null;
    });
}
